import type { WritableSignal } from '@angular/core'

import type { ChatMessage, ChatMessageFile } from '@flowx/angular-ui-toolkit'
import { getFileExtension } from '@flowx/angular-ui-toolkit'
import {
  type ChatMessageContent,
  type ChatMessageFeedback,
  ChatMessageType,
  type PendingVoiceMessage,
  buildAudioMessagePayload,
  config,
  createPendingVoiceMessage,
  pendingVoiceMatchesMessage,
  resolveMessageTimestamp,
  resolveVoiceChatSessionId,
  uploadVoiceBlob,
} from '@flowx/core-sdk'

// --- Voice upload lifecycle ---

export async function uploadAngularVoice(
  voice: PendingVoiceMessage,
  pendingVoice: WritableSignal<PendingVoiceMessage | null>,
  audioUrlCache: Map<string, string>,
  sendMessage: (message: string, messageContent?: ChatMessageContent) => void,
  onUploadError?: () => void
): Promise<void> {
  pendingVoice.set({ ...voice, status: 'sending' })

  try {
    const { downloadPath, downloadUrl, filePath, mimeType } = await uploadVoiceBlob(
      voice.chatSessionId,
      voice.blob
    )

    // Seed cache so the audio resolves instantly. `PendingVoiceMessage.blob` also covers
    // React Native's file reference, which has no object URL — Angular only ever records
    // through `MediaRecorder`, so anything else is left for the server URL to resolve.
    if (voice.blob instanceof Blob) {
      audioUrlCache.set(downloadUrl, URL.createObjectURL(voice.blob))
    }
    pendingVoice.set({ ...voice, status: 'sent', downloadUrl, downloadPath, mimeType })

    const audioPayload = buildAudioMessagePayload(downloadPath, voice.duration, mimeType, filePath)
    sendMessage('', audioPayload)
  } catch (error) {
    console.error('[VoiceUpload] Upload failed:', error)
    pendingVoice.set({ ...voice, status: 'error' })
    onUploadError?.()
  }
}

export function handleAngularVoiceMessage(
  event: { blob: Blob; duration: number },
  pendingVoice: WritableSignal<PendingVoiceMessage | null>,
  audioUrlCache: Map<string, string>,
  sendMessage: (message: string, messageContent?: ChatMessageContent) => void,
  onUploadError?: () => void
): void {
  const chatSessionId = resolveVoiceChatSessionId()
  const voice = createPendingVoiceMessage(event.blob, event.duration, chatSessionId)
  uploadAngularVoice(voice, pendingVoice, audioUrlCache, sendMessage, onUploadError)
}

export function retryAngularVoice(
  pendingVoice: WritableSignal<PendingVoiceMessage | null>,
  audioUrlCache: Map<string, string>,
  sendMessage: (message: string, messageContent?: ChatMessageContent) => void,
  onUploadError?: () => void
): void {
  const pending = pendingVoice()
  if (!pending) {
    return
  }
  uploadAngularVoice(pending, pendingVoice, audioUrlCache, sendMessage, onUploadError)
}

// --- Cache cleanup ---

export function cleanupAudioUrlCache(cache: Map<string, string>): void {
  cache.forEach((blobUrl) => {
    if (blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl)
    }
  })
  cache.clear()
}

// --- Display messages merge ---

export function mergeAngularDisplayMessages(
  msgs: ChatMessage[],
  pending: PendingVoiceMessage | null,
  handleRetryVoice: () => void
): ChatMessage[] {
  if (!pending || pending.status === 'sent') {
    if (pending?.downloadUrl) {
      const hasMatch = msgs.some((m) => m.sender === 'sender' && m.audioUrl)
      if (hasMatch) {
        return msgs
      }
    }
    if (!pending) {
      return msgs
    }
  }

  const pendingMsg: ChatMessage = {
    id: pending.id,
    content: '',
    sender: 'sender',
    timestamp: new Date(),
    voiceMessageStatus: pending.status,
    audioDuration: pending.duration,
    onRetryVoice: handleRetryVoice,
    ...(pending.downloadUrl ? { audioUrl: pending.downloadUrl } : {}),
  }

  return [...msgs, pendingMsg]
}

// --- Normalize chat history with audio detection ---

export function normalizeChatHistoryMessage(
  item: {
    _id: string
    actor: string
    message: string
    messageId?: string
    feedback?: ChatMessageFeedback | null
    timestamp?: string
    hasError?: boolean
    type?: ChatMessageType
    audioFile?: { downloadPath: string; duration?: number }
    files?: { downloadPath: string; mimeType?: string; originalFileName?: string }[]
  },
  audioUrlCache: Map<string, string>,
  localTimestamps?: Map<string, Date>
): ChatMessage {
  const isAudio = item.type === ChatMessageType.AUDIO
  let audioUrl: string | undefined
  if (isAudio && item.audioFile?.downloadPath) {
    const { apiUrl } = config()
    audioUrl = `${apiUrl}/document/${item.audioFile.downloadPath}`
  }

  const isAttachments = item.type === ChatMessageType.ATTACHMENTS
  let files: ChatMessageFile[] | undefined
  if (isAttachments && item.files?.length) {
    const { apiUrl } = config()
    files = item.files.map((file) => {
      const name = file.originalFileName || file.downloadPath.split('/').pop() || file.downloadPath
      return {
        name,
        extension: getFileExtension(name),
        mimeType: file.mimeType ?? '',
        downloadUrl: `${apiUrl}/document/${file.downloadPath}`,
      }
    })
  }

  return {
    id: item._id,
    messageId: item.messageId,
    feedback: item.feedback ?? null,
    content: isAudio ? '' : item.message,
    sender: (item.actor === 'human' ? 'sender' : 'receiver') as 'sender' | 'receiver',
    ...(localTimestamps
      ? { timestamp: resolveMessageTimestamp(item._id, item.timestamp, localTimestamps) }
      : {}),
    hasError: item.hasError,
    ...(audioUrl ? { audioUrl: audioUrlCache.get(audioUrl) ?? audioUrl } : {}),
    ...(isAudio && item.audioFile?.duration ? { audioDuration: item.audioFile.duration } : {}),
    ...(files ? { files } : {}),
    type: item.type,
  }
}

// --- Auto-clear pending voice ---

export function checkAndClearAngularPendingVoice(
  normalizedMessages: ChatMessage[],
  pendingVoice: WritableSignal<PendingVoiceMessage | null>
): void {
  const pending = pendingVoice()
  if (!pending?.downloadUrl) {
    return
  }
  if (pendingVoiceMatchesMessage(normalizedMessages, pending.downloadUrl)) {
    pendingVoice.set(null)
  }
}
